import { useCallback } from "react";
import { useLanguage, type SupportedLanguage } from "./LanguageProvider";
import en from "./translations/en.json";
import th from "./translations/th.json";

type TranslationValue = string | number | boolean | null | undefined | TranslationMap | TranslationValue[];
type TranslationMap = Record<string, TranslationValue>;
type Replacements = Record<string, string | number>;

type Dictionaries = Record<SupportedLanguage, TranslationMap>;

const dictionaries: Dictionaries = {
  en: en as TranslationMap,
  th: th as TranslationMap,
};

const resolveKey = (map: TranslationMap, key: string): TranslationValue => {
  return key.split(".").reduce<TranslationValue>((acc, segment) => {
    if (acc == null) return undefined;
    if (Array.isArray(acc)) return undefined;
    if (typeof acc !== "object") return undefined;
    const next = (acc as TranslationMap)[segment];
    return next;
  }, map);
};

const formatValue = (value: TranslationValue, replacements?: Replacements): string => {
  if (value == null) {
    return "";
  }

  if (typeof value !== "string") {
    return String(value);
  }

  if (!replacements) {
    return value;
  }

  return Object.entries(replacements).reduce((acc, [token, replacement]) => {
    return acc.replaceAll(`{${token}}`, String(replacement));
  }, value);
};

export const useTranslation = () => {
  const { language } = useLanguage();

  const translate = useCallback((key: string, replacements?: Replacements): string => {
    const dictionary = dictionaries[language] ?? dictionaries.en;
    const fallbackDictionary = dictionaries.en;

    const rawValue = resolveKey(dictionary, key) ?? resolveKey(fallbackDictionary, key);

    if (rawValue === undefined) {
      return key;
    }

    return formatValue(rawValue, replacements);
  }, [language]);

  return { t: translate, language };
};

export type { Replacements as TranslationReplacements };
